import moment from 'moment';
import React, { Dispatch, SetStateAction, useEffect } from 'react';
import { rtaNames } from '../components/common/FatcaCheck.types';
import {
    getMotilalFortnightlyEndDate,
    getMotilalFortnightlyInstallmentDiff,
    getNextWeekdayOnOrAfter,
    isMotilalFortnightly,
    isMotilalWeekly,
} from './motilalSipHelpers';

type sipHookProps = {
    stpFlag?: boolean;
    frequencyType: string;
    kfinFlag: boolean;
    numInstallments: number;
    sipDay: string;
    sipTat: number;
    sipDates?: Array<string | number>;
    setErrors?: Dispatch<any>;
    perpetual?: boolean;
    amc?: string;
    applyMotilalRules?: boolean;
    // allowed days of the week (0 = Sunday .. 6 = Saturday) for named-weekday
    // weekly schemes, e.g. Nippon "every wednesday". Empty for numeric date arrays.
    allowedSipWeekdays?: Array<number>;
    // allowed days of the month for day-of-month weekly schemes, e.g. [1,8,15,22].
    // Empty for weekday-named / "All" arrays.
    allowedSipMonthDays?: Array<number>;
};

enum WeekDays {
    Monday = 1,
    Tuesday = 2,
    Wednesday = 3,
}

export enum freqType {
    W = 'Weekly',
    M = 'Monthly',
    D = 'Daily',
    DZ = 'DailyZip',
    Q = 'Quarterly',
    H = 'Halfyearly',
    A = 'Annual',
    F = 'Fortnightly',
}

export function monthDiff(dateFrom: Date, dateTo: Date) {
    const newDateFrom = moment(dateFrom, 'MM/DD/YYYY');
    const newDateTo = moment(dateTo, 'MM/DD/YYYY');
    return newDateTo.diff(newDateFrom, 'months');
}

function weekDiff(dateFrom: Date, dateTo: Date) {
    const newDateFrom = moment(dateFrom, 'MM/DD/YYYY');
    const newDateTo = moment(dateTo, 'MM/DD/YYYY');
    return newDateTo.diff(newDateFrom, 'weeks');
}
function daysIntoYear(date: Date) {
    return (
        (Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) -
            Date.UTC(date.getFullYear(), 0, 0)) /
        24 /
        60 /
        60 /
        1000
    );
}

function daysInMonth(month: any, year: any) {
    return new Date(year, month, 0).getDate();
}
function parseAllowedSipDays(sipDates?: Array<string | number>) {
    if (!sipDates || !sipDates.length) return [] as number[];
    return sipDates
        .map((day) => Number(day))
        .filter((day) => !isNaN(day) && day >= 1 && day <= 31)
        .sort((a, b) => a - b);
}

function getNextAllowedWeeklyDate(currentDate: Date, allowedDays: number[]) {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const day = currentDate.getDate();
    const daysThisMonth = allowedDays.filter(
        (allowedDay) => allowedDay > day && allowedDay <= daysInMonth(month + 1, year),
    );
    if (daysThisMonth.length > 0) {
        return new Date(year, month, daysThisMonth[0]);
    }
    let nextMonth = month + 1;
    let nextYear = year;
    if (nextMonth > 11) {
        nextMonth = 0;
        nextYear += 1;
    }
    while (true) {
        const daysNextMonth = allowedDays.filter(
            (allowedDay) => allowedDay <= daysInMonth(nextMonth + 1, nextYear),
        );
        if (daysNextMonth.length > 0) {
            return new Date(nextYear, nextMonth, daysNextMonth[0]);
        }
        nextMonth += 1;
        if (nextMonth > 11) {
            nextMonth = 0;
            nextYear += 1;
        }
    }
}

// Returns the first cycle date on or after minDate.
function getFirstCycleDateOnOrAfter(minDate: Date, allowedMonthDays: number[]): Date {
    const year = minDate.getFullYear();
    const month = minDate.getMonth();
    const day = minDate.getDate();
    const eligibleThisMonth = allowedMonthDays.filter(
        (d) => d >= day && d <= daysInMonth(month + 1, year),
    );
    if (eligibleThisMonth.length > 0) {
        return new Date(year, month, eligibleThisMonth[0]);
    }
    let nextMonth = month + 1;
    let nextYear = year;
    if (nextMonth > 11) {
        nextMonth = 0;
        nextYear += 1;
    }
    while (true) {
        const eligibleNextMonth = allowedMonthDays.filter(
            (d) => d <= daysInMonth(nextMonth + 1, nextYear),
        );
        if (eligibleNextMonth.length > 0) {
            return new Date(nextYear, nextMonth, eligibleNextMonth[0]);
        }
        nextMonth += 1;
        if (nextMonth > 11) {
            nextMonth = 0;
            nextYear += 1;
        }
    }
}

function calculateWeeklyEndDate(
    startDate: Date,
    numInstallments: number,
    sipDates?: Array<string | number>,
) {
    const allowedDays = parseAllowedSipDays(sipDates);
    if (!allowedDays.length || numInstallments <= 1) {
        return new Date(startDate);
    }
    let currentDate = new Date(startDate);
    let count = 1;
    while (count < numInstallments) {
        currentDate = getNextAllowedWeeklyDate(currentDate, allowedDays);
        count += 1;
    }
    return currentDate;
}

enum Day {
    Sunday,
    Monday,
    Tuesday,
    Wednesday,
    Thursday,
    Friday,
    Saturday,
}

function getDaysToAdd(currentDay: Day) {
    if (currentDay === Day.Sunday) return 1;
    if (currentDay === Day.Saturday) return 2;
}

export function isWeekendDay(currentDay: Day) {
    return currentDay === Day.Saturday || currentDay === Day.Sunday;
}
function addWorkingDaysFromTomorrow(startDate: Date, days: number): Date {
    const result = new Date(startDate);
    let added = 0;

    while (added < days) {
        result.setDate(result.getDate() + 1); // move to next day
        const day = result.getDay();
        if (day !== Day.Saturday && day !== Day.Sunday) {
            added++;
        }
    }
    return result;
}

// Move a date forward (at most one week) to the next day of the week that is in
// allowedWeekdays. Returns the date unchanged when there is no weekday constraint
// or it already lands on an allowed weekday.
function getNextAllowedWeekday(date: Date, allowedWeekdays: Array<number>): Date {
    const result = new Date(date);
    if (!allowedWeekdays || allowedWeekdays.length === 0) return result;
    for (let i = 0; i < 7; i++) {
        if (allowedWeekdays.includes(result.getDay())) return result;
        result.setDate(result.getDate() + 1);
    }
    return result;
}

// Move a date forward (up to ~2 months) to the next day-of-month that is in
// allowedMonthDays, e.g. [1, 8, 15, 22]. Returns the date unchanged when there is
// no constraint or it already lands on an allowed day-of-month.
function getNextAllowedMonthDay(date: Date, allowedMonthDays: Array<number>): Date {
    const result = new Date(date);
    if (!allowedMonthDays || allowedMonthDays.length === 0) return result;
    for (let i = 0; i < 62; i++) {
        if (allowedMonthDays.includes(result.getDate())) return result;
        result.setDate(result.getDate() + 1);
    }
    return result;
}

const useSip = ({
    frequencyType,
    numInstallments,
    sipDay,
    sipTat,
    sipDates,

    setErrors,
    stpFlag = false,
    perpetual,
    kfinFlag,
    amc,
    applyMotilalRules = false,
    allowedSipWeekdays = [],
    allowedSipMonthDays = [],
}: sipHookProps): [
        Date,
        Date,
        Dispatch<SetStateAction<Date>>,
        Dispatch<SetStateAction<Date>>,
        any,
        number,
    ] => {
    const [startDate, setStartDate] = React.useState<Date>(new Date());
    const [endDate, setEndDate] = React.useState<Date>(new Date());

    const handleError = (value: boolean) => {
        setErrors &&
            setErrors((prevErrors: any) => {
                return {
                    ...prevErrors,
                    dateError: value,
                };
            });
    };
    let installment_diff = 0,
        monthMultiplier = 1,
        dayMultiplier = 1;
    switch (frequencyType.replace(/[^a-zA-Z]/g, '')) {
        case freqType.M:
            installment_diff = monthDiff(startDate, endDate);
            break;
        case freqType.H:
            installment_diff = monthDiff(startDate, endDate) / 6.0;
            monthMultiplier = 6;
            break;
        case freqType.Q:
            installment_diff = monthDiff(startDate, endDate) / 3.0;
            monthMultiplier = 3;
            break;
        case freqType.W:
            installment_diff = weekDiff(startDate, endDate);
            dayMultiplier = 7;
            break;
        case freqType.F: {
            const motilalDiff =
                applyMotilalRules && isMotilalFortnightly(amc, frequencyType)
                    ? getMotilalFortnightlyInstallmentDiff(startDate, endDate)
                    : NaN;
            installment_diff = !isNaN(motilalDiff)
                ? motilalDiff
                : weekDiff(startDate, endDate) / 2.0;
            dayMultiplier = 14;
            break;
        }
        case freqType.A:
            installment_diff = monthDiff(startDate, endDate) / 12.0;
            monthMultiplier = 12;
            break;
        case freqType.D:
        case freqType.DZ:
            let dft = endDate.getTime() - startDate.getTime();
            installment_diff = dft / (1000 * 3600 * 24);
            break;
        default:
            throw new Error(`${frequencyType} is not supported`);
    }
    useEffect(() => {
        if (!!sipDay && !!sipTat) {
            let todayDate = new Date();
            if (kfinFlag && (frequencyType === freqType.W || frequencyType === freqType.F)) todayDate.setHours(0, 0, 0, 0);
            // date after which the sip must happen
            let sipTatDate = new Date(todayDate);
            sipTatDate.setDate(sipTatDate.getDate() + Number(sipTat));
            const isJmmf = amc && String(amc) === '105';
            if (isJmmf) {
                const jmmfStart = addWorkingDaysFromTomorrow(todayDate, Number(sipTat));
                setStartDate(jmmfStart);
                return;
            }

            if (applyMotilalRules && isMotilalWeekly(amc, frequencyType)) {
                const motilalStart = getNextWeekdayOnOrAfter(sipTatDate, parseInt(sipDay));
                if (motilalStart) {
                    setStartDate(motilalStart);
                    return;
                }
            }

            // named-weekday weekly/fortnightly schemes (e.g. Nippon 'every wednesday'):
            // start on the next allowed weekday on/after the min-TAT date, only for KFIN.
            if (kfinFlag && (frequencyType === freqType.W || frequencyType === freqType.F) && allowedSipWeekdays.length > 0) {
                setStartDate(getNextAllowedWeekday(sipTatDate, allowedSipWeekdays));
                return;
            }

            if (
                allowedSipMonthDays.length > 0 &&
                String(amc) === 'RMF' &&
                frequencyType === freqType.W
            ) {
                setStartDate(getFirstCycleDateOnOrAfter(sipTatDate, allowedSipMonthDays));
                return;
            }

            const selectedSipDate = new Date(
                todayDate.getFullYear(),
                todayDate.getMonth(),
                parseInt(sipDay),
            );
            let newStartDate;
            if (selectedSipDate >= sipTatDate) {
                // if sip date is after the tat date
                newStartDate = selectedSipDate;
            } else {
                // if sip date is before the tat date
                selectedSipDate.setMonth(sipTatDate.getMonth() + 1);
                newStartDate = selectedSipDate;
            }
            if (!stpFlag && [freqType.D, freqType.DZ].includes(frequencyType as freqType)) {
                const startDateDayOfWeek = newStartDate.getDay();
                const addDays = getDaysToAdd(startDateDayOfWeek) || 0;
                if (addDays > 0) {
                    setStartDate(moment(newStartDate, 'MM/DD/YYYY').add(addDays, 'days').toDate());
                } else {
                    setStartDate(newStartDate);
                }
                return;
            }
            setStartDate(newStartDate);
        }
    }, [sipDay, sipTat, frequencyType, amc, JSON.stringify(allowedSipWeekdays)]);

    const resetEndDate = () => {
        if (applyMotilalRules && isMotilalFortnightly(amc, frequencyType)) {
            const motilalEnd = getMotilalFortnightlyEndDate(startDate, numInstallments);
            if (motilalEnd) {
                setEndDate(motilalEnd);
                handleError(false);
                return;
            }
        }
        if (!stpFlag && [freqType.D].includes(frequencyType as freqType) && kfinFlag) {
            const newEndDate = moment(startDate, 'MM/DD/YYYY')
                .add((numInstallments - 1) * dayMultiplier, 'days')
                .toDate();
            // check for saturday and sunday
            const endDateDayofWeek = newEndDate.getDay();
            const addDays = getDaysToAdd(endDateDayofWeek) || 0;
            if (addDays > 0) {
                setEndDate(moment(newEndDate, 'MM/DD/YYYY').add(addDays, 'days').toDate());
            } else {
                setEndDate(newEndDate);
            }
            handleError(false);
            return;
        }
        const allowedWeeklySipDays = parseAllowedSipDays(sipDates);
        const isNipponWeeklyFlow =
            frequencyType === freqType.W &&
            String(amc) === 'RMF' &&
            allowedWeeklySipDays.length > 0;
        if (isNipponWeeklyFlow) {
            setEndDate(calculateWeeklyEndDate(startDate, numInstallments, sipDates));
            handleError(false);
            return;
        }
        if (
            frequencyType == freqType.W ||
            frequencyType == freqType.D ||
            frequencyType == freqType.DZ ||
            frequencyType === freqType.F
        ) {
            let computedEndDate = moment(startDate, 'MM/DD/YYYY')
                .add((numInstallments - 1) * dayMultiplier, 'days')
                .toDate();
            // keep the weekly/fortnightly end date within the sip dates array: named-day arrays
            // snap to an allowed weekday, day-of-month arrays snap to an allowed date, only for KFIN.
            if (kfinFlag && (frequencyType == freqType.W || frequencyType === freqType.F)) {
                if (allowedSipWeekdays.length > 0) {
                    computedEndDate = getNextAllowedWeekday(computedEndDate, allowedSipWeekdays);
                } else if (allowedSipMonthDays.length > 0) {
                    computedEndDate = getNextAllowedMonthDay(computedEndDate, allowedSipMonthDays);
                }
            }
            setEndDate(computedEndDate);
            handleError(false);
            return;
        }
        setEndDate(
            moment(startDate, 'MM/DD/YYYY')
                .add((numInstallments - 1) * monthMultiplier, 'months')
                .toDate(),
        );
        handleError(false);
    };
    useEffect(() => {
        if (!perpetual) {
            resetEndDate();
        }
    }, [startDate, frequencyType, sipDay, numInstallments, sipDates, amc, applyMotilalRules]);
    useEffect(() => {
        parseInt(installment_diff.toString()) < numInstallments - 1
            ? handleError(true)
            : handleError(false);
    }, [endDate]);
    return [
        startDate,
        endDate,
        setStartDate,
        setEndDate,
        resetEndDate,
        Math.ceil(installment_diff),
    ];
};
export default useSip;
